import {memo, useState} from 'react'
import {GitCommitIcon, UnfoldIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {CommitAuthorStack} from '@github-ui/commit-attribution'
import type {CommitsProps} from '@github-ui/commits/shared/Commits'
import {CopySHA} from '@github-ui/commits/components/CopySHA'
import {shortSha} from '@github-ui/commits/utils/short-sha'
import {pullRequestChangesCommitRangePath} from '@github-ui/paths'
import {useLayoutBaseRepository, useLayoutPullRequestNumber} from '../../stores/layout-selectors'
import type {ConversationCommitGroup} from './utils/interleave-commits'
import styles from './CommitDisclosure.module.css'

type Commit = CommitsProps['commitGroups'][number]['commits'][number]
type CommitVisibilityGroup = {commits: [Commit, ...Commit[]]; collapsed: boolean}

function groupCommitsByVisibility(commits: Commit[], visibleCommitOids: string[]): CommitVisibilityGroup[] {
  const visibleCommitOidSet = new Set(visibleCommitOids)
  const groups: CommitVisibilityGroup[] = []

  for (const commit of commits) {
    const collapsed = !visibleCommitOidSet.has(commit.oid)
    const previousGroup = groups.at(-1)

    if (collapsed && previousGroup?.collapsed) {
      previousGroup.commits.push(commit)
    } else {
      groups.push({commits: [commit], collapsed})
    }
  }

  return groups
}

function focusCommitTitle(element: HTMLAnchorElement | null) {
  element?.focus()
}

const CompactCommit = memo(function CompactCommitRow({
  commit,
  focusOnMount = false,
}: {
  commit: Commit
  focusOnMount?: boolean
}) {
  const repository = useLayoutBaseRepository()
  const number = useLayoutPullRequestNumber()
  const href = pullRequestChangesCommitRangePath({
    owner: repository.ownerLogin,
    repo: repository.name,
    number,
    head: commit.oid,
  })
  const authors =
    commit.committerAttribution && commit.committer ? [...commit.authors, commit.committer] : commit.authors

  return (
    <div className={styles.CommitRow} data-testid="timeline-commit-row">
      <GitCommitIcon className={styles.Icon} />
      <CommitAuthorStack authors={authors} />
      <a
        ref={focusOnMount ? focusCommitTitle : undefined}
        href={href}
        className={styles.Title}
        aria-label={`Commit: ${commit.shortMessage}`}
      >
        {commit.shortMessage}
      </a>
      <div className={styles.Actions}>
        <Link href={href} className={styles.Sha} aria-label={`Commit ${shortSha(commit.oid)}`}>
          {shortSha(commit.oid)}
        </Link>
        <CopySHA sha={commit.oid} />
      </div>
    </div>
  )
})

const CollapsedCommits = memo(function CollapsedCommitGroup({commits}: Pick<CommitVisibilityGroup, 'commits'>) {
  const [expanded, setExpanded] = useState(false)
  const firstCommit = commits[0]

  const authors = [
    ...new Set(commits.flatMap(commit => commit.authors.map(author => author.login || author.displayName))),
  ]
  const count = `${commits.length} additional commits`
  const summary = `${count}${authors.length > 0 ? ` by ${authors.join(', ')}` : ''}`
  if (expanded) {
    return commits.map((commit, index) => <CompactCommit key={commit.oid} commit={commit} focusOnMount={index === 0} />)
  }

  return (
    <details onToggle={event => event.currentTarget.open && setExpanded(true)}>
      <summary className={styles.Summary} aria-label={`${summary}, starting at commit ${firstCommit.oid.slice(0, 7)}`}>
        <UnfoldIcon className={styles.Icon} />
        <span>
          {count}
          {authors.length > 0 && (
            <>
              {' by '}
              <strong>{authors.join(', ')}</strong>
            </>
          )}
        </span>
      </summary>
    </details>
  )
})

const CommitGroup = memo(function CommitGroupRows({commits, collapsed}: CommitVisibilityGroup) {
  const firstCommit = commits[0]
  if (!collapsed || commits.length === 1) {
    return <CompactCommit commit={firstCommit} />
  }

  return <CollapsedCommits commits={commits} />
})

export function CommitDisclosure({
  commitGroups,
  visibleCommitOids,
}: Pick<ConversationCommitGroup, 'commitGroups' | 'visibleCommitOids'>) {
  const commits = commitGroups.flatMap(group => group.commits)
  const visibilityGroups = groupCommitsByVisibility(commits, visibleCommitOids)

  return (
    <div data-testid="timeline-commit-group">
      {visibilityGroups.map(group => (
        <CommitGroup key={group.commits[0].oid} {...group} />
      ))}
    </div>
  )
}
